import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { SectionHeading, Watermark } from '../components/Section';
import { useSiteInfo } from '../lib/settingsContext';
import { submitContact } from '../lib/api';
import SEO from '../lib/SEO';

export default function Contact() {
  const site = useSiteInfo();
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', service: '', message: '' });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing fields', description: 'Please fill in name, email and message.' });
      return;
    }
    setSending(true);
    try {
      await submitContact(form);
      toast({ title: 'Message sent', description: 'We’ll respond within one business day.' });
      setForm({ name: '', company: '', email: '', phone: '', service: '', message: '' });
    } catch (err) {
      toast({
        title: 'Could not send',
        description: err?.response?.data?.detail || 'Please try again in a moment.',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="font-sans-body">
      <SEO jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Contact Apex Quality Control',
        url: '/contact',
      }} />
      <section className="relative overflow-hidden">
        <Watermark opacity={0.04} />
        <div className="container-aqc relative py-16 lg:py-20 text-center">
          <span className="eyebrow">Contact</span>
          <h1 className="mt-5 font-serif text-[2.4rem] sm:text-[3rem] leading-[1.08] font-semibold text-aqc-navy">Contact</h1>
          <p className="mt-4 text-aqc-muted max-w-2xl mx-auto">
            Contact us for information about our inspection, testing, auditing, certification, and quality-control services. We’ll respond within one business day.
          </p>
        </div>
      </section>

      <section id="form" className="py-12 bg-aqc-cream">
        <div className="container-aqc grid lg:grid-cols-3 gap-8">
          <form onSubmit={submit} className="lg:col-span-2 bg-white border border-aqc-line rounded-2xl p-7 shadow-sm">
            <h2 className="font-serif text-2xl text-aqc-navy">Send us a message</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input id="name" value={form.name} onChange={set('name')} required className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="company">Company</Label>
                <Input id="company" value={form.company} onChange={set('company')} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={form.email} onChange={set('email')} required className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={form.phone} onChange={set('phone')} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="service">Service of interest</Label>
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="— Select —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quality-inspection">Quality Inspection</SelectItem>
                    <SelectItem value="product-testing">Product Testing</SelectItem>
                    <SelectItem value="vendor-audit">Vendor Audit</SelectItem>
                    <SelectItem value="cargo-survey">Cargo Survey & Loading</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea id="message" rows={5} value={form.message} onChange={set('message')} required className="mt-1.5" />
              </div>
            </div>
            <Button type="submit" disabled={sending} className="mt-6 bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white h-11 px-6">
              {sending ? 'Sending…' : (<><Send size={16} className="mr-1.5" /> Send Message</>)}
            </Button>
          </form>

          <aside className="space-y-4">
            <div className="bg-white border border-aqc-line rounded-2xl p-6">
              <div className="h-10 w-10 rounded-full bg-aqc-red/10 text-aqc-red flex items-center justify-center"><Mail size={18} /></div>
              <h3 className="font-serif text-lg text-aqc-navy mt-3">Email</h3>
              <a href={`mailto:${site.email}`} className="text-aqc-red font-medium">{site.email}</a>
            </div>
            <div className="bg-white border border-aqc-line rounded-2xl p-6">
              <div className="h-10 w-10 rounded-full bg-aqc-red/10 text-aqc-red flex items-center justify-center"><Phone size={18} /></div>
              <h3 className="font-serif text-lg text-aqc-navy mt-3">Phone</h3>
              <a href={`tel:${site.phoneRaw}`} className="text-aqc-red font-medium">{site.phone}</a>
            </div>
            <div className="bg-white border border-aqc-line rounded-2xl p-6">
              <div className="h-10 w-10 rounded-full bg-aqc-red/10 text-aqc-red flex items-center justify-center"><MapPin size={18} /></div>
              <h3 className="font-serif text-lg text-aqc-navy mt-3">Address</h3>
              <p className="text-aqc-ink/80 text-sm mt-1 leading-relaxed">
                {site.address.line1}<br />
                {site.address.line2}<br />
                {site.address.city}<br />
                {site.address.country}
              </p>
            </div>
            <div className="bg-aqc-navy text-white rounded-2xl p-6">
              <div className="flex items-center gap-2"><Clock size={16} /><span className="text-sm">{site.hours}</span></div>
              <p className="mt-3 text-sm text-white/80">We reply within one business day.</p>
            </div>
          </aside>
        </div>

        <div className="container-aqc mt-10">
          <div className="relative h-[340px] rounded-2xl overflow-hidden border border-aqc-line bg-aqc-navy">
            <div className="absolute inset-0 opacity-30 bg-[url('https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&w=1600&q=60')] bg-cover bg-center" />
            <div className="absolute inset-0 flex items-center justify-center text-white text-center px-4">
              <div>
                <MapPin size={26} className="mx-auto" />
                <div className="mt-2 font-serif text-xl">Mogadishu, Somalia</div>
                <div className="text-white/70 text-sm">Map location — set from the admin panel.</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
